import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import { getConsent } from "./lib/consent";
import "./index.css";

// Error tracking. Initializes only when (a) VITE_SENTRY_DSN is set AND
// (b) the user has accepted analytics/diagnostics consent. A user who
// declines, or who hasn't chosen yet, gets no error telemetry. Accepting
// takes effect on the next page load (init runs once at startup). The
// DSN is a public client key (safe to ship in the bundle).
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn && getConsent() === "accepted") {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    // Capture a fraction of transactions for performance monitoring.
    // Low by default to stay within the free tier; bump if needed.
    tracesSampleRate: 0.1,
    // Only send errors in production builds; dev noise isn't useful.
    enabled: import.meta.env.PROD,
  });
}

// Service worker update hygiene. The PWA precaches the app shell, so
// without this a phone can keep running a weeks-old bundle indefinitely:
// a cold launch serves the OLD cached shell while the new worker installs
// in the background, and an installed app warm-resumed from the app
// switcher never checks for updates at all (this replayed already-fixed
// bugs on mobile). Two measures:
// 1. When an updated worker takes control mid-session (skipWaiting +
//    clientsClaim), reload once so THIS session runs the new code.
// 2. Check for updates whenever the app returns to the foreground, the
//    moment standalone iOS apps otherwise skip.
if ("serviceWorker" in navigator) {
  let hadController = !!navigator.serviceWorker.controller;
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // The first controller is the initial install, not an update.
    if (!hadController) {
      hadController = true;
      return;
    }
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      navigator.serviceWorker
        .getRegistration()
        .then((reg) => reg?.update())
        .catch(() => {
          // Offline or registration unavailable; a skipped check is fine.
        });
    }
  });
}

// Graceful fallback for render crashes. Without an error boundary, a
// thrown error during render unmounts the whole tree and the user sees
// a blank white page (what happened with the missing-prop bug). This
// shows a recoverable message instead, and Sentry.ErrorBoundary also
// guarantees the error is reported. Works even when Sentry isn't
// initialized (it just won't report in that case).
const ErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center p-6 text-center bg-background">
    <div className="max-w-sm">
      <h1 className="text-xl font-serif font-bold mb-2">
        Something went wrong
      </h1>
      <p className="text-sm text-ink-muted mb-5">
        The page hit an unexpected error. Reloading usually fixes it. If it
        keeps happening, your trips are saved locally and won't be lost.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="bg-accent hover:bg-accent-hover text-on-accent rounded-full px-6 py-2 text-sm font-medium transition-colors"
      >
        Reload
      </button>
    </div>
  </div>
);

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <App />
  </Sentry.ErrorBoundary>
);
